import intervalPlural from 'i18next-intervalplural-postprocessor';
import { initReactI18next, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { PropsWithChildren, useEffect, FC } from 'react';
import Backend from 'i18next-http-backend';
import { lsManager } from '@shared/utils';
import { useAuth } from '@shared/hooks';
import i18n from 'i18next';

export const languages = ['en', 'pl', 'ru', 'uk'] as const;
export type Language = (typeof languages)[number];

export const navigatorLanguage = navigator.language.split('-')[0];
export const fallbackLng = languages.includes(navigatorLanguage as Language)
  ? navigatorLanguage
  : 'pl';

i18n
  .use(Backend)
  .use(new LanguageDetector(null, { lookupLocalStorage: lsManager.getFullKey('LANGUAGE') }))
  .use(initReactI18next)
  .use(intervalPlural)
  .init({
    backend: { loadPath: `${import.meta.env.VITE_BASE_PATH}/locales/{{lng}}/{{ns}}.json` },
    interpolation: { escapeValue: false },
    supportedLngs: languages,
    fallbackLng,
  });

export default i18n;

export const LanguageProvider: FC<PropsWithChildren> = ({ children }) => {
  const { getLanguage } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    const updateLanguage = async () => {
      const lastKeycloakLanguage = lsManager.get('KEYCLOAK_LANGUAGE');
      const locale = await getLanguage();

      // if language is different from last keycloak language, we need to update the language and save it to local storage
      // if not it means that user already changed language in the app and we don't want to override it
      if (locale && languages.includes(locale as Language) && lastKeycloakLanguage !== locale) {
        lsManager.set('KEYCLOAK_LANGUAGE', locale);
        lsManager.set('LANGUAGE', locale);
        i18n.changeLanguage(locale);
      }
    };

    updateLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
};
